import { useLocalization } from '@magicdoorinc/ui';
import { useLocation } from '@solidjs/router';
import { createMemo, createSignal, onCleanup, Show } from 'solid-js';
import MenuHamburger from '~/assets/images/common/menuHamburger.svg?component-solid';
import { CompletionBarInNavigation } from '~/components/Onboarding/V3/components/CompletionBar';
import TrialBanner from '~/components/common/TrialBanner';
import { useMenu } from '~/components/layouts/Menu/MenuContext';
import { useOnboarding } from '~/contexts/global';
import { useIsMobileWidth } from '~/hooks/useIsMobileWidth';
import { useIsScreenWide } from '~/hooks/useIsScreenWide';
import BreadcrumbContainer from './BreadcrumbContainer/BreadcrumbContainer';
import { CustomLayoutButton } from './CustomLayoutButton/CustomLayoutButton';
import SearchBar from './MagicSearchBar/MagicSearch';
import { SearchProvider } from './MagicSearchBar/MagicSearchContext';
import ProfileButton from './ProfileButton/ProfileButton';
import { WhatsNewButton } from './WhatsNewButton/WhatsNewButton';
interface NavigationProps {
  setIsSearchActive: (isSearchActive: boolean) => void;
}

const Navigation = (props: NavigationProps) => {
  const menu = useMenu();
  const { t } = useLocalization();
  const { isOnboardingCompleted } = useOnboarding();
  const isWide = useIsScreenWide();
  const isMobileWidth = useIsMobileWidth();

  const location = useLocation();
  const isHomePage = createMemo(() => ['/'].some((item) => item === location.pathname));

  // The search bar is portaled out and positioned from the right edge, so it has
  // to know how much room the buttons beside it take up. Measuring beats a fixed
  // offset: the cluster's contents vary by route and screen width, and a hardcoded
  // value silently strands the search bar whenever a button is added or removed
  // (which is what the language button's removal did).
  const [rightClusterInset, setRightClusterInset] = createSignal(0);
  const observeRightCluster = (element: HTMLDivElement) => {
    // Measure to the viewport's right edge, not just the cluster's own width: the
    // bar is positioned from that edge, so the row's horizontal padding counts too.
    // Otherwise the leftover padding shows up as extra gap beside the bar.
    const measure = () => setRightClusterInset(window.innerWidth - element.getBoundingClientRect().left);
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    observer.observe(document.body);
    onCleanup(() => observer.disconnect());
  };

  const handleTechSupportClick = () => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  };

  return (
    <SearchProvider>
      <div
        class="fixed top-0 z-30 flex w-full flex-col bg-white shadow-sm"
        classList={{
          'md:w-layout-right': !menu?.isManuallyCollapsed(),
          'md:w-layout-full': menu?.isManuallyCollapsed(),
        }}>
        <div
          class="relative flex w-full items-center px-2 md:ml-0 lg:px-3"
          classList={{ 'h-14': isMobileWidth(), 'h-16': !isMobileWidth() }}>
          <div class="flex items-center gap-2 md:hidden">
            <div onClick={() => menu?.setIsSidebarOpen((prev) => !prev)} class="cursor-pointer rounded-lg bg-gray-100 p-2">
              <Show when={!menu?.isSidebarOpen()} fallback={<MenuHamburger class="size-5 rotate-180 text-primary" />}>
                <MenuHamburger class="size-5 text-text-02" />
              </Show>
            </div>

            <div class="block h-4 w-px md:hidden" />
          </div>

          <div class="mr-auto max-w-[70%] overflow-hidden">
            <BreadcrumbContainer mobileVariant="currentOnly" />
          </div>

          <SearchBar setIsSearchActive={props.setIsSearchActive} rightClusterInset={rightClusterInset()} />

          <div class="flex items-center gap-2" ref={observeRightCluster}>
            <Show when={!isMobileWidth()}>
              <div class="h-4 w-px bg-border" />
            </Show>
            <WhatsNewButton />
            <Show when={!menu?.isMobile() && isHomePage() && isOnboardingCompleted()}>
              <CustomLayoutButton />
            </Show>
            <button
              type="button"
              class="flex h-[38px] items-center justify-center gap-2 rounded-full border border-separator px-2.5 text-sm text-text-02 transition-colors hover-allowed:hover:bg-light-pink hover-allowed:hover:text-primary"
              classList={{
                'py-2.5': !isWide(),
                'py-1.5': isWide(),
              }}
              onClick={handleTechSupportClick}>
              <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
              <Show when={isWide()}>{t('Support')}</Show>
            </button>
            <ProfileButton />
          </div>
        </div>
      </div>

      <TrialBanner />
      <CompletionBarInNavigation />
    </SearchProvider>
  );
};

export default Navigation;
